import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuota } from "@/hooks/useQuota";
import { supabase } from "@/lib/supabase";

export function Pricing() {
  const { t } = useTranslation();
  const { quota } = useQuota();
  const [plan, setPlan] = useState<"monthly" | "annual">("annual");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPro = quota?.plan === "pro" || quota?.plan === "team";

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "stripe-checkout",
        { body: { plan_type: plan } }
      );
      if (fnError) throw fnError;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  if (isPro) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="text-4xl mb-4">&#10003;</div>
        <h2 className="text-xl font-bold text-redi-text mb-2">
          {t("paywall.welcomeTitle")}
        </h2>
        <p className="text-redi-muted text-sm">
          {t("paywall.welcomeMsg")}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-8">
        <span className="inline-block px-3 py-1 bg-redi-primary/20 text-redi-primary text-xs font-bold rounded-full mb-3">
          PRO
        </span>
        <h2 className="text-2xl font-bold text-redi-text mb-2">
          {t("paywall.screenTitle")}
        </h2>
        <p className="text-redi-muted text-sm">
          {t("paywall.headline")}
        </p>
      </div>

      {/* Feature comparison */}
      <div className="bg-redi-surface rounded-xl p-5 mb-6">
        <div className="grid grid-cols-3 gap-2 text-sm mb-4">
          <div />
          <div className="text-center text-redi-muted font-medium">Free</div>
          <div className="text-center text-redi-primary font-medium">Pro</div>
        </div>
        {[
          { label: t("paywall.compareMeetings"), free: t("paywall.compareMeetingsFree"), pro: t("paywall.compareMeetingsPro") },
          { label: t("paywall.compareTemplates"), free: "1", pro: "5" },
          { label: t("paywall.compareExport"), free: "-", pro: "PDF" },
          { label: t("paywall.compareSupport"), free: "-", pro: "&#10003;" },
        ].map(({ label, free, pro }) => (
          <div
            key={label}
            className="grid grid-cols-3 gap-2 text-sm py-2 border-t border-white/5"
          >
            <div className="text-redi-text">{label}</div>
            <div className="text-center text-redi-muted">{free}</div>
            <div
              className="text-center text-redi-primary"
              dangerouslySetInnerHTML={{ __html: pro }}
            />
          </div>
        ))}
      </div>

      {/* Plan selector */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setPlan("monthly")}
          className={`flex-1 p-4 rounded-xl border-2 transition-colors ${
            plan === "monthly"
              ? "border-redi-primary bg-redi-primary/5"
              : "border-white/10 hover:border-white/20"
          }`}
        >
          <p className="text-sm font-semibold text-redi-text">
            {t("paywall.planMonthly")}
          </p>
          <p className="text-xl font-bold text-redi-primary">4,99 &euro;</p>
          <p className="text-xs text-redi-muted">/mois</p>
        </button>
        <button
          onClick={() => setPlan("annual")}
          className={`flex-1 p-4 rounded-xl border-2 transition-colors relative ${
            plan === "annual"
              ? "border-redi-primary bg-redi-primary/5"
              : "border-white/10 hover:border-white/20"
          }`}
        >
          <span className="absolute -top-2 right-3 px-2 py-0.5 bg-redi-accent text-white text-xs font-bold rounded-full">
            -33%
          </span>
          <p className="text-sm font-semibold text-redi-text">
            {t("paywall.planAnnual")}
          </p>
          <p className="text-xl font-bold text-redi-primary">39,99 &euro;</p>
          <p className="text-xs text-redi-muted">/an</p>
        </button>
      </div>

      {/* CTA */}
      <button
        onClick={handleCheckout}
        disabled={loading}
        className="w-full py-4 bg-redi-primary text-white font-bold text-lg rounded-xl hover:bg-redi-primary/80 transition-colors disabled:opacity-50"
      >
        {loading ? "..." : t("paywall.subscribe")}
      </button>

      {/* Trust badges */}
      <div className="flex justify-center gap-6 mt-6 text-xs text-redi-muted">
        <span>RGPD</span>
        <span>{t("paywall.trustCancel")}</span>
        <span>{t("paywall.trustSecure")}</span>
      </div>

      {error && (
        <p className="text-redi-error text-sm text-center mt-4">{error}</p>
      )}
    </div>
  );
}
